import React, { useState, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Typography,
  TableHead,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Paper,
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Input,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { I18n } from "aws-amplify/utils";
import { getStoresColumns } from "layouts/dashboard/components/AlarmDetailsContent/columns";
import { fetchAlarmsOnSameDevice } from "layouts/dashboard/apiService";
import Draggable from "react-draggable";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { 
  LocalizationProvider, 
  MobileDateTimePicker, 
  DateTimeField } 
from "@mui/x-date-pickers";
import SearchIcon from '@mui/icons-material/Search';
dayjs.extend(utc);
dayjs.extend(timezone);

function alarmsSameDevReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

const initialState = {
  loading: true,
  data: null,
  error: null,
};

const durationOptions = [
  { value: 1, label: "1 hour" },
  { value: 3, label: "3 hours" },
  { value: 6, label: "6 hours" },
  { value: 12, label: "12 hours" },
  { value: 24, label: "24 hours" },
  { value: "1w", label: "Last 7 days" },
  { value: "21d", label: "Last 21 days" },
  { value: "28d", label: "Last 28 days" },
  { value: "custom", label: I18n.get("Select Date") },
];

const AlarmsOnSameStorePopup = ({ open, onClose, data, language }) => {
  const [duration, setDuration] = useState(24); // Default to 24 hours
  const [alarmsSameDevState, dispatchAlarmsSameDev] = useReducer(
    alarmsSameDevReducer,
    initialState
  );
  const [alarmsSameDevPage, setAlarmsSameDevPage] = useState(0);
  const [rowsPerAlarmsSameDevPage, setRowsPerAlarmsSameDevPage] = useState(50);
  const [startDateCustom, setStartDateCustom] = useState(dayjs().tz().format("YYYY-MM-DD 00:00:00"));
  const [endDateCustom, setEndDateCustom] = useState(dayjs().tz().format("YYYY-MM-DD 01:00:00"));
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [searchCustom, setSearchCustom] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };
  const [filteredRows, setFilteredRows] = useState([]);

  useEffect(() => {
    if (open) {
      if(duration === "custom" && searchCustom) {
        const fetchData = async () => {
          dispatchAlarmsSameDev({ type: "LOADING" });
          try {
            let startDateTimeUTC;
            let endDateTimeUTC;
            startDateTimeUTC = dayjs(startDateCustom).utc().format();
            endDateTimeUTC = dayjs(endDateCustom).utc().format();

            const response = await fetchAlarmsOnSameDevice(
              data[0]?.account_number, // Utilizziamo l'account_number se disponibile
              language,
              startDateTimeUTC,
              endDateTimeUTC
            );
            dispatchAlarmsSameDev({ type: "DATA_RECEIVED", value: response });
          } catch (error) {
            dispatchAlarmsSameDev({ type: "ERROR", error });
          }
        };
        fetchData();
        setSearchCustom(false);
      } else if (duration !== "custom") {
        const fetchData = async () => {
          dispatchAlarmsSameDev({ type: "LOADING" });
          try {
            const now = dayjs().utc();
            let startDateTimeUTC;
            let endDateTimeUTC;
            if (duration === "1w") {
              endDateTimeUTC = now.format();
              startDateTimeUTC = now.subtract(7, "day").format();
            } else if (duration === "21d") {
              endDateTimeUTC = now.format();
              startDateTimeUTC = now.subtract(21, "day").format();
            } else if (duration === "28d") {
              endDateTimeUTC = now.format();
              startDateTimeUTC = now.subtract(28, "day").format();
            } else {
              endDateTimeUTC = now.format();
              startDateTimeUTC = now.subtract(duration, "hour").format();
            }

            const response = await fetchAlarmsOnSameDevice(
              data[0]?.account_number, // Utilizziamo l'account_number se disponibile
              language,
              startDateTimeUTC,
              endDateTimeUTC
            );

            dispatchAlarmsSameDev({ type: "DATA_RECEIVED", value: response });
          } catch (error) {
            dispatchAlarmsSameDev({ type: "ERROR", error });
          }
        };

        fetchData();
      }
    }
  }, [open, duration, data, language, searchCustom]);

  useEffect(() => {
    if (searchText) {
      const filtered = alarmsSameDevState.data.filter((row) => {
        return Object.values(row).some((val) =>
          String(val).toLowerCase().includes(searchText.toLowerCase())
        );
      });
      setFilteredRows(filtered);
    } else {
      setFilteredRows(alarmsSameDevState.data);
    }
  },[alarmsSameDevState.data, searchText]);

  const handleDurationChange = (event) => {
    if (event.target.value !== "custom") {
      setDuration(event.target.value);
      setShowCustomDate(false);
      setSearchCustom(false); 
    }else {
      setDuration(event.target.value);
      setShowCustomDate(true);
    }
  };

  const handlePageChange = (event, newPage) => {
    setAlarmsSameDevPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerAlarmsSameDevPage(parseInt(event.target.value, 10));
    setAlarmsSameDevPage(0);
  };

  const formatTimestamp = (value) => {
    if (!value) return "";
    return dayjs.utc(value).tz(dayjs.tz.guess()).format("YYYY-MM-DD HH:mm:ss");
  };

  // Function to format duration. If it starts with "0 days" of has "00h", we only show the time part.
  const formatDuration = (value) => {
    if(value !== null && value !== undefined){
      let timePart = value;
      if (value.startsWith("0 days")) {
        timePart = timePart.split(",")[1]?.trim();
      }
      if (value.includes("00h")){
        timePart = timePart.replace("00h:", "");
      }
      // If duration is negative, remove unecessary minus signs
      if (value.includes("-")){
        timePart = timePart.replaceAll("-", "");
        timePart = "-" + timePart;
      }
      return timePart ? timePart : null;
    }
    return value;
  }

  const columns = getStoresColumns(); // Using dynamic columns

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false} // Disabilita i limiti predefiniti di larghezza
      fullWidth
      PaperComponent={PaperComponent}
      sx={{
        "& .MuiDialog-paper": {
          width: "120%", // Imposta la larghezza desiderata come percentuale
          maxWidth: "2400px", // Puoi specificare anche un massimo per garantire che non diventi troppo grande
        },
      }}
    >
      <DialogTitle style={{ cursor: "move" }}>
        {data[0]?.original_store !== undefined ? (
          <Typography variant="h5">
          {data[0]?.original_store + " - " + I18n.get("Alarms on Same Store - ") +
            (duration === "1w" ? I18n.get("Last 7 Days") : duration === "custom" ? I18n.get("Selected date") : duration === "21d" ? I18n.get("Last 21 Days") : duration === "28d" ? I18n.get("Last 28 Days") : `Last ${duration}H`)}
          </Typography>
        ) : (
          <Typography variant="h5">
          {data[0]?.store_name + " - " + I18n.get("Alarms on Same Store - ") +
            (duration === "1w" ? I18n.get("Last 7 Days") : duration === "custom" ? I18n.get("Selected date") : duration === "21d" ? I18n.get("Last 21 Days") : duration === "28d" ? I18n.get("Last 28 Days") : `Last ${duration}H`)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent> 
        <Box display="flex" alignItems="center" gap={1} marginBottom={2} justifyContent={"space-between"}>
        <FormControl fullWidth margin="normal" sx={{ maxWidth: 200 }}>
          <InputLabel>{I18n.get("Duration")}</InputLabel>
          <Select
            label="Duration"
            value={duration}
            onChange={handleDurationChange}
          >
            {durationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ width: "200px" }}>
          <Input
            type="text"
            placeholder={I18n.get("Search")}
            onChange={searchChanged}
            //fullWidth={tableParentIsMD}
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Box>
        </Box>
        { showCustomDate && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              gap={1}
              marginTop={2}
            >
              <DateTimeField
                label={I18n.get("Start Date")}
                format="YYYY/MM/DD HH:mm"
                value={dayjs(startDateCustom)}
                onChange={(newDate) => {
                  const formattedDate = dayjs(newDate).format("YYYY-MM-DD HH:mm:ss");
                  setStartDateCustom(formattedDate);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      style: {
                        padding: "4px",
                        fontSize: "14px",
                      },
                    }}
                  />
                )}
              />
              <DateTimeField
                label={I18n.get("End Date")}
                format="YYYY/MM/DD HH:mm"
                value={dayjs(endDateCustom)}
                onChange={(newDate) => {
                  const formattedDate = dayjs(newDate).format("YYYY-MM-DD HH:mm:ss");
                  setEndDateCustom(formattedDate);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      style: {
                        padding: "4px",
                        fontSize: "14px",
                      },
                    }}
                  />
                )}
              />
            <IconButton
              onClick={() => {setDuration("custom"); setSearchCustom(true);}}
              //onClick={() => {setSearchCustom(true);}}
              size="large"
              color="primary"
            >
              <SearchIcon />
            </IconButton>
            </Box>
          </LocalizationProvider>
        )}
        
        {alarmsSameDevState.loading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : alarmsSameDevState.error ? (
          <Typography color="error">
            {alarmsSameDevState.error.message}
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {//alarmsSameDevState.data
                filteredRows
                  ?.slice(
                    alarmsSameDevPage * rowsPerAlarmsSameDevPage,
                    alarmsSameDevPage * rowsPerAlarmsSameDevPage +
                      rowsPerAlarmsSameDevPage
                  )
                  .map((alarm) => (
                    <TableRow key={alarm.id}>
                      {columns.map((column) => (
                        <TableCell key={column.id}>
                          {column.id === "datetime_start" && alarm[column.id]
                            ? formatTimestamp(alarm[column.id]) // Format timestamp
                            : column.id === "duration"
                            ? formatDuration(alarm.duration) // Format duration
                            : column.rendercell
                            ? column.rendercell({
                                value: alarm[column.id],
                                row: alarm,
                              })
                            : alarm[column.id]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={alarmsSameDevState.data?.length || 0}
          page={alarmsSameDevPage}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerAlarmsSameDevPage}
          rowsPerPageOptions={[5, 10, 25, 50]} // Opzioni personalizzate
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </DialogContent>
      <DialogActions style={{ cursor: "move" }}>
        <Button onClick={onClose} color="primary">
          {I18n.get("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AlarmsOnSameStorePopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  //data: PropTypes.object,
  language: PropTypes.string,
};

export default AlarmsOnSameStorePopup;
